import { observer } from "@legendapp/state/react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Heart, MessageSquareText, LayoutList } from "lucide-react";
import { captionsVisible$, wordlistModalOpen$ } from "../../state/videoState";
import { playbackRate$, cycleSpeed, likedChapters$ } from "../../state/userState";

interface ActionBarProps {
  chapterId: number;
  onToggleLike: () => void;
}

// Top-to-bottom order mirrors iOS FloatingActionButtonsView (ToggleButton.swift):
// like → speed → wordlist → captions. Play/pause is NOT in this rail on iOS — it
// lives in the centre of the video (see CenterPlayPause).
const ActionBar = observer(function ActionBar({ chapterId, onToggleLike }: ActionBarProps) {
  const rate = playbackRate$.get();
  const isLiked = !!likedChapters$[chapterId]?.get();
  const captionsOn = captionsVisible$.get();
  const wordlistEnabled = useFeatureFlagEnabled("wordlist_in_feed");

  return (
    <div
      className="flex flex-col items-center gap-0"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onToggleLike}
        className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
      >
        {/* iOS uses heart.fill — a SOLID heart, white when not liked, red when
            liked (not an outline). */}
        <Heart
          className={`w-8 h-8 drop-shadow-md ${
            isLiked ? "fill-red-500 stroke-red-500" : "fill-white stroke-white"
          }`}
        />
      </button>

      <button
        type="button"
        onClick={cycleSpeed}
        aria-label="Playback speed"
        className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
      >
        <span className="text-xl font-semibold text-white drop-shadow-md">
          {rate}x
        </span>
      </button>

      {wordlistEnabled && (
        <button
          type="button"
          onClick={() => wordlistModalOpen$.set(true)}
          aria-label="Mark words you know"
          className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
        >
          {/* iOS list.bullet.rectangle — a bulleted list inside a rounded rect. */}
          <LayoutList className="w-7 h-7 text-white drop-shadow-md" />
        </button>
      )}

      {/* iOS captions.bubble — a speech bubble with subtitle lines; dimmed off. */}
      <button
        type="button"
        onClick={() => captionsVisible$.set((v) => !v)}
        aria-label={captionsOn ? "Hide captions" : "Show captions"}
        className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
      >
        <MessageSquareText
          className={`w-7 h-7 drop-shadow-md ${captionsOn ? "text-white" : "text-white/50"}`}
        />
      </button>
    </div>
  );
});

export { ActionBar };
